import theme from '../../theme'
import { IProps, MessageResponse } from './types'
import Navbar from '../Navbar'
import ChatList from '../ChatList'
import ChatInput from '../ChatInput'
import React, { useEffect, useRef, useState } from 'react'
import { useStyles } from '../../style/styles'
import { Box, Button, Grid, IconButton, ThemeProvider, Typography, useMediaQuery } from '@mui/material'
import * as utils from '../../lib/messageUtils'
import { useDispatch, useSelector } from 'react-redux'
import { ChannelData, ChannelDetails, ChannelInfo, RootState } from '../../redux/channel/types'
import { setChannelData,setChannelDetails,updateChannelMessageList,updateTermsAgreed } from '../../redux/channel/actions'
import messagingService from '../../services/MessagingService'
import { MessagingConstants } from '../../lib/enums'
import getDisplayName from '../../lib/getDisplayName'
import JobDescription from '../../components/JobDescription'
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import { logInfo, logError } from '../../lib/rumLogger'
import Notifications from '../Notifications'
import CloseIcon from '@mui/icons-material/Close';

const Chat = (props: IProps) => {
  const { sender, receiver, channel, job, connections, token, platform, apply_date, deviceType, websocketConnection } = props
  const classes = useStyles()
  const dispatch = useDispatch()
  const loggedInUserArn = sender?.messaging_id
  const [isEndedConversation, setIsEndedConversation] = useState(false)
  const channelsReducer = useSelector((state: RootState) => state.channels.channels)
  const [channelData, setChannelReducer] = useState<ChannelData>()
  const [shouldLoadMessages, setShouldLoadMessages] = useState(false)
  const [selectedTimezone, setSelectedTimezone] = useState<string | null>(sender?.timezone)
  const [adminID, setAdminId] = useState<string>(loggedInUserArn)
  const [endOrReenableActionDoer, setEndOrReenableActionDoer] = useState<string | null>(null)
  const [updatedChannel, setUpdatedChannel] = useState<any>(channel)
  const [updatedConnections, setUpdatedConnections] = useState<any>(connections)
  const [showChat, setShowChat] = useState<boolean>(true)
  const [isJobDescriptionOpen, setIsJobDescriptionOpen] = useState(false)
  const [isLastMessageSender, setIsLastMessageSender] = useState<boolean>(true)
  const [hasReadLastMessage, setHasReadLastMessage] = useState(true)
  const [initialNextToken, setInitialNextToken] = useState<string | null>(null)
  const isMobile = useMediaQuery("(max-width: 700px)") || platform === "mobile"
  const [showOptInBanner, setShowOptInBanner] = useState(false);
  const isSourcer = utils.isSourcerChannel(channel?.Metadata)

  useEffect(() => {
    if(channel) {
      mapChannelDetails()
      fetchChannel()
    }
    if(channel?.metadata){
      setAdminId(JSON.parse(channel?.Metadata).Admin)
    }
  }, [channel])

  useEffect(() => {
    setChannelReducer(channelsReducer[channel.Name])
    if (shouldLoadMessages) {
      handleLoadMessages()
      setShouldLoadMessages(false)
    }
  }, [channelsReducer, shouldLoadMessages])

  useEffect(() =>{
    if(sender) {
      utils.setAwsSession(sender?.messaging_id)
      /* STS expiry temp: 15 mins (buffer: 5 mins) */
      const tokenExpiryinMs = 10 * 60 * 1000
      const interval = setInterval(() => {
        utils.setAwsSession(sender?.messaging_id);
      }, tokenExpiryinMs);
      return () => clearInterval(interval);
    }
  }, [sender])
  
  useEffect(() => {
    messagingService.subscribeToMessageUpdate(callbackProcessor)
    return () => {
      messagingService.unsubscribeFromMessageUpdate(callbackProcessor)
    }
  }, [])

  const toggleJobDescription = () => {
    setIsJobDescriptionOpen(true);
  };

  const handleCloseJobDescription = () => {
    setIsJobDescriptionOpen(false);
  };
  
  const [isNotificationsDialogOpen, setNotificationsDialogOpen] = useState(false);
  const handleOpenNotificationsDialog = () => {
    setNotificationsDialogOpen(true);
  };

  const handleCloseNotificationsDialog = () => {
    setNotificationsDialogOpen(false);
  };
 
    useEffect(() => {
    if (!sender || !sender.messaging_id || sender.role?.toLowerCase() !== "jobseeker") return;

    const fetchNumbers = async () => {
      const data = await utils.fetchJobseekerNumbers(sender.messaging_id);

      const formatted = data.map((item: any) => ({
        id: item.PhoneID,
        number: item.PhoneNumber.Number,
        countryCode: item.PhoneNumber.CountryCode,
        type: item.PhoneNumber.Type,
        isOptedIn: item.SmsOptIn,
      }));

      const noPhones = formatted.length === 0;
      const noneOptedIn = formatted.length > 0 && formatted.every((p: { isOptedIn: any }) => !p.isOptedIn);

      setShowOptInBanner(noPhones || noneOptedIn);
    };

    fetchNumbers();
  }, [sender?.messaging_id, sender?.role]);


  const fetchChannel = async () => {
    /* REFACTOR: REFACTOR WHEN OPEN SEARCH IS IMPLEMENTED */
    let channelDetails = await utils.getChannelDetails(channel?.ChannelArn, sender?.messaging_id)
    let channelMembershipDetails = await utils.getChannelMembershipDetails(channel?.ChannelArn, sender?.messaging_id)
    channelDetails = channelDetails?.Channel
    channelMembershipDetails = channelMembershipDetails?.ChannelMembership?.AppInstanceUserMembershipSummary
    const userHasReadLastMessage = utils.compareTimestamp(channelDetails?.LastMessageTimestamp, channelMembershipDetails?.ReadMarkerTimestamp)
    setHasReadLastMessage(userHasReadLastMessage)

    if(channelDetails) {
      const metadata = JSON.parse(channelDetails?.Metadata)
      const isEndedConversation = metadata?.EndConversationTimestamp ? true : false
      setUpdatedChannel(channelDetails)
      setAdminId(metadata?.Admin)
      setIsEndedConversation(isEndedConversation)
      setEndOrReenableActionDoer(metadata?.EndOrReenableActionDoer)
      const blockedBy = metadata?.BlockedBy
      if (blockedBy && blockedBy.trim() !== '') {
        blockedBy == loggedInUserArn ?
          setUpdatedConnections({ isBlocked: false, isBlockedLink: true}) :
          setUpdatedConnections({ isBlocked: true, isBlockedLink: false})
      } else {
        setUpdatedConnections({ isBlocked: connections.isBlocked, isBlockedLink: connections.isBlockedLink})
      }
      return channelDetails
    } return null
  }

  const callbackProcessor = async(message: MessageResponse) => {
    const messageType = message?.headers['x-amz-chime-event-type']
    const record = JSON.parse(message?.payload)
    const isRecordNotEmpty = record && record?.Content
    switch(messageType) {
      case 'CREATE_CHANNEL_MESSAGE':
          // if (record.Type === 'CONTROL' || record.Persistence === 'NON_PERSISTENT') {
          //   return
          // }
        if(isRecordNotEmpty) {
          if(utils.checkIfBlockIndicatorMessage(record.Content)) {
            fetchChannel()
          } else if(sender?.messaging_id === utils.splitUserArn(record?.Sender?.Arn)) {
              if(sender.role && sender?.role.toLowerCase() === 'recruiter') {
                setHasReadLastMessage(prev => !prev)
              } else {
                setHasReadLastMessage(true)
              }
          }
          else if(sender?.messaging_id !== utils.splitUserArn(record?.Sender?.Arn))  {
            if(sender?.terms_agreed) {
              utils.updateReadMarker(sender?.messaging_id, channel?.ChannelArn, false, receiver)
              if(sender.role && sender?.role.toLowerCase() === 'recruiter') {
                setHasReadLastMessage(prev => !prev)
              } else {
                setHasReadLastMessage(true)
              }
            }
          }
          handleLoadMessages()
          setShouldLoadMessages(true)
        }
        break
      case 'CREATE_CHANNEL_MEMBERSHIP':
      case 'UPDATE_CHANNEL':
        fetchChannel()
        handleLoadMessages()
        setShouldLoadMessages(true)
        break
      case 'REDACT_CHANNEL_MESSAGE':
      case 'UPDATE_CHANNEL_MESSAGE':
        fetchChannel()
        handleLoadMessages()
        setShouldLoadMessages(true)
        break
      case 'SESSION_ESTABLISHED':
        let receiverData = { externalUserId: receiver.external_user_id || '', messagingId: receiver.messaging_id || '', role: receiver.role || ''}
        const senderData = { externalUserId: sender.external_user_id || '', messagingId: sender.messaging_id || '', role: sender.role || ''}
        utils.sendChannelMessage(
          senderData as {
            externalUserId: string;
            messagingId: string;
            role: string;
          },
          receiverData as {
            externalUserId: string;
            messagingId: string;
            role: string;
          },
          channel.ChannelArn,
          '',
          '',
          true
        );
        break
    }
  }

  const mapChannelDetails = async () => { // previously setChannelInfo()
    if(channel) {
      const listMessages = await utils.listChannelMessages(channel.ChannelArn, loggedInUserArn)
      const messages = listMessages && listMessages.Messages ? listMessages.Messages : null
      setInitialNextToken(listMessages?.NextToken || null)

      const channelData: any = {
        [channel.Name]: {
          channelArn: channel.ChannelArn,
          totalUnreadCount: 0,
          messageList: messages,
          userInfo: sender
        }
        }
        setChannelReducer(channelData)
        dispatch(setChannelData(channelData))
    } else {
       const channelData: any = {
                [channel.Name]: {
                  channelArn: null,
                  totalUnreadCount: 0,
                  messageList: [],
                  userInfo: sender
                }
              }
        setChannelReducer(channelData)
        dispatch(setChannelData(channelData))
    }
  }

  const handleLoadMessages = async () => {
    if (channelData && channelData?.channelArn) {
      try {
        const listMessages = await utils.listChannelMessages(channelData.channelArn, loggedInUserArn)
        const messages = listMessages?.Messages || null
        setInitialNextToken(listMessages?.NextToken || null)

        if (messages) {
          const updatedChannelsReducer = { ...channelsReducer }
          const updatedMessageList = [...messages]
          updatedChannelsReducer[channel.Name] = {
            ...updatedChannelsReducer[channel.Name],
            messageList: updatedMessageList
          }
          dispatch(setChannelData(updatedChannelsReducer))
        }
      } catch (error) {
        logError(error as Error, 'handleLoadMessages', { channelArn: channelData?.channelArn })
      }
    }
  };

  const handleEndConversation = async() => {
    if(channel.ChannelArn) {
      const actionMarker = isEndedConversation ? MessagingConstants.REENABLE_CONVERSATION_MARKER : MessagingConstants.END_CONVERSATION_MARKER
      if (loggedInUserArn) {
        const res = await utils.updateChannelMarkerBackend(
          channel.ChannelArn,
          loggedInUserArn,
          adminID,
          actionMarker,
          null,
          receiver)
        setIsEndedConversation(!isEndedConversation)
        setEndOrReenableActionDoer(loggedInUserArn)
        window.parent.postMessage({ type: actionMarker, jobseekerId: receiver.external_user_id, jobId: job.job_id }, '*');
        handleLoadMessages()
      }
    }
  }

  return (loggedInUserArn) ? (
    <>
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css?family=Poppins:300,400,500,600,700"/>
      <ThemeProvider theme={theme}>
        <div className={ isMobile ? classes.mainQuickConnectContainerMobile : classes.mainQuickConnectContainer}>
          <Grid  >
            <Grid item>
              <Navbar
                receiver={receiver}
                userType={"Jobseeker"}
                username={getDisplayName(receiver, true)}
                onEndConversation={handleEndConversation}
                isEndedConversation={isEndedConversation}
                onNotificationClick={handleOpenNotificationsDialog}
                token={token}
                connections={connections}
                userArn={sender.messaging_id}
                endOrReenableActionDoer={endOrReenableActionDoer}
                job={job}
                deviceType={deviceType}
                channelMetadata={channel?.metadata}
                websocketConnection={websocketConnection}
              />
            </Grid>

            {showChat ? (
              <>
                <Grid item>
                  <div className={isMobile ? classes.quickConnectChatBodyMobile : classes.quickConnectChatBody}>
                    {isMobile && (
                      <Grid
                        item
                        container
                        alignContent='center'
                        className={classes.chatDescriptionContainer}
                        sx={{ marginBottom: "5px", cursor: "pointer"}}
                        onClick={toggleJobDescription}>
                        <Typography className={`${classes.chatDescription} ${classes.multiLine}`}>
                          <span className={classes.chevronContainer}>
                            {isSourcer ? (
                              job?.title && (
                                <span className={classes.primaryText}> About:
                                  <span className={classes.textCapitalize}>{' ' + job?.title}</span> @
                                  <span className={classes.textCapitalize}>{' ' + job?.companyName}</span>
                                </span>
                              )
                            ) : (
                              <span className={classes.primaryText}> Applied for
                                <span className={classes.textCapitalize}>{' ' + job?.title}</span> @
                                <span className={classes.textCapitalize}>{' ' + job?.companyName}</span>
                              </span>
                            )}
                            <ExpandMoreOutlinedIcon className={classes.chevronIcon} />
                          </span>
                        </Typography>
                      </Grid>
                    )}

                    {isJobDescriptionOpen && isMobile && (
                      <>
                        <JobDescription platform="mobile" apply_date={apply_date} handleClose={handleCloseJobDescription} job={job} isSourcer={isSourcer}/>
                        <div className={classes.overlay}></div>
                      </>
                    )}

                    {showOptInBanner && (
                      <Box display="flex" justifyContent="flex-end" width="100%">
                        <Box
                          component="div"
                          sx={{
                            backgroundColor: '#e3f2fd',
                            border: '1px solid #bbdefb',
                            px: 1.5,
                            py: 0.5,
                            boxShadow: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            width: '100%',
                          }}
                        >
                          <Typography
                            component="span"
                            sx={{
                              fontSize: '0.8rem',
                              color: 'text.secondary',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Don't miss a response from this recruiter —{' '}
                          </Typography>

                          <Button
                            variant="text"
                            color="primary"
                            size="small"
                            onClick={() => {
                              setShowOptInBanner(false);
                              handleOpenNotificationsDialog();
                            }}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 500,
                              padding: 0,
                              fontSize: '0.8rem',
                              minWidth: 'auto',
                              ml: 0.5,
                            }}
                          >
                            Opt-In to SMS
                          </Button>

                          <IconButton
                            size="small"
                            aria-label="dismiss"
                            onClick={() => setShowOptInBanner(false)}
                            sx={{
                              p: 0.3,
                              color: 'text.secondary',
                              ml: 0.8,
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    )}

                    <ChatList
                      channelArn={channel?.ChannelArn || null}
                      channelName={channel?.Name}
                      senderUserArn={loggedInUserArn}
                      isEndedConversation={isEndedConversation}
                      userType={"Jobseeker"}
                      onLoadMessages={handleLoadMessages}
                      connections={updatedConnections}
                      inputFocused={false}
                      timezone={selectedTimezone}
                      channel={updatedChannel}
                      channelMetadata={channel.metadata ? JSON.parse(channel.metadata) : null}
                      endOrReenableActionDoer={endOrReenableActionDoer}
                      receiver={receiver}
                      job={job}
                      sender={sender}
                      setIsLastMessageSender={setIsLastMessageSender}
                      hasReadLastMessage={hasReadLastMessage}
                      setHasReadLastMessage={setHasReadLastMessage}
                      initialNextToken={initialNextToken}
                    />
                  </div>
                </Grid>
                <Grid item>
                  <ChatInput
                    onFocus={() => {}}
                    connections={updatedConnections}
                    channel={{
                      channelArn: channel.ChannelArn || null,
                      channelName: channel.Name,
                      metadata: channel.metadata,
                      isEndedConversation: isEndedConversation
                    }}
                    updatedChannel={updatedChannel}
                    job={job}
                    receiver={{
                      info: {
                        first_name: receiver.first_name,
                        last_name: receiver.last_name,
                        email:"",
                        mobile:  null
                      },
                      externalUserId: receiver.messaging_id,
                      role: "Recruiter",
                      userArn: receiver.messaging_id
                    }}
                    sender={{
                      info: {
                        first_name: sender.first_name,
                        last_name: sender.last_name,
                        email: "",
                        mobile:  null
                      },
                      externalUserId: sender.messaging_id,
                      role: "Jobseeker",
                      userArn: sender.messaging_id
                    }}
                    onLoadMessages={handleLoadMessages}
                    adminID={adminID}
                    endOrReenableActionDoer={endOrReenableActionDoer}
                    hasReadLastMessage={hasReadLastMessage}
                    setHasReadLastMessage={setHasReadLastMessage}
                    isLastMessageSender={isLastMessageSender}
                  />
                </Grid>
              </>
            ) : null}
          </Grid>

          <Notifications
          openPopIn={isNotificationsDialogOpen} 
          onChange={handleOpenNotificationsDialog}
          onClose={handleCloseNotificationsDialog}
          messagingUserId={sender.messaging_id}
          initialValue={sender?.timezone}
        />
        </div>
      </ThemeProvider>
    </>
  ) : null
}

export default Chat
